import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface JournalEntry {
  date: string;
  mood: string;
  emotions: string;
  skillsUsed: string;
  challenges: string;
  victories: string;
}

export const useJournal = () => {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadEntries();
  }, []);

  const ensureUserExists = async (userId: string, userEmail: string) => {
    const { data: existingUser, error: checkError } = await supabase
      .from('User')
      .select('id')
      .eq('id', userId)
      .maybeSingle();

    if (checkError) throw checkError;

    if (!existingUser) {
      // Create user record if it doesn't exist
      const { error: createError } = await supabase
        .from('User')
        .insert({ 
          id: userId,
          email: userEmail 
        });

      if (createError) throw createError;
    }
  };

  const loadEntries = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('Journal')
        .select('*')
        .eq('userId', session.user.id)
        .order('date', { ascending: false });

      if (error) throw error;

      if (data) {
        setEntries(data.map(entry => ({
          date: entry.date,
          mood: entry.mood || "",
          emotions: entry.emotions || "",
          skillsUsed: entry.skillsUsed || "",
          challenges: entry.challenges || "",
          victories: entry.victories || "",
        })));
      }
    } catch (error: any) {
      console.error('Error loading journal entries:', error);
      toast({
        title: "Error loading journal entries",
        description: "Failed to load your journal entries. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveEntry = async (entry: JournalEntry) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to save your journal entry",
          variant: "destructive",
        });
        return;
      }

      // Ensure user exists in User table before saving journal entry
      await ensureUserExists(session.user.id, session.user.email || '');

      const { data, error } = await supabase
        .from('Journal')
        .insert({
          userId: session.user.id,
          ...entry,
        })
        .select()
        .single();

      if (error) throw error;

      setEntries(prev => [data, ...prev]);
      toast({
        title: "Journal entry saved",
        description: "Your journal entry has been saved successfully.",
      });
    } catch (error: any) {
      console.error('Error saving journal entry:', error);
      toast({
        title: "Error saving journal entry",
        description: "Failed to save your journal entry. Please try again later.",
        variant: "destructive",
      });
    }
  };

  return {
    entries,
    isLoading,
    saveEntry,
  };
};